import { useState, useEffect } from 'react';
import { supplierReconSessionsAPI } from '../lib/api';
import { FiX, FiDownload, FiLoader, FiFileText, FiFile, FiExternalLink } from 'react-icons/fi';

function getFileType(mimeType, fileName) {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  if (mimeType === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
       'application/vnd.ms-excel', 'text/csv'].includes(mimeType) ||
      ['xlsx', 'xls', 'csv'].includes(ext)) return 'excel';
  if (['application/vnd.openxmlformats-officedocument.wordprocessingml.document',
       'application/msword'].includes(mimeType) ||
      ['docx', 'doc'].includes(ext)) return 'word';
  if (mimeType?.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  return 'other';
}

export default function FilePreviewModal({ doc, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const fileType = getFileType(doc.mime_type, doc.file_name);
  const canPreview = fileType === 'pdf' || fileType === 'image';

  useEffect(() => {
    if (canPreview) {
      loadFile();
    } else {
      setLoading(false);
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id]);

  const loadFile = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await supplierReconSessionsAPI.downloadDocument(doc.id);
      setObjectUrl(URL.createObjectURL(res.data));
    } catch (err) {
      setError('Failed to load file preview.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await supplierReconSessionsAPI.downloadDocumentForSave(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const typeLabels = {
    excel: { label: 'Excel Spreadsheet', icon: FiFileText, color: 'text-green-600' },
    word:  { label: 'Word Document',     icon: FiFileText, color: 'text-blue-600' },
    other: { label: 'File',              icon: FiFile,     color: 'text-gray-500' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-4xl max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <p className="text-sm font-medium text-gray-900 truncate pr-4" title={doc.file_name}>
            {doc.file_name}
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {downloading ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiDownload className="w-3.5 h-3.5" />}
              Download
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-64 gap-2 text-gray-400">
              <FiLoader className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading preview...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
              <FiFile className="w-10 h-10" />
              <p className="text-sm text-red-500">{error}</p>
              <button
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <FiDownload className="w-4 h-4" /> Download instead
              </button>
            </div>
          ) : canPreview && objectUrl ? (
            fileType === 'pdf' ? (
              // Chrome on HTTPS blocks blob: in iframe/object — open in new tab instead
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <FiFileText className="w-14 h-14 text-red-400" />
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700">PDF ready to view</p>
                  <p className="text-xs text-gray-400 mt-1">Opens in a new browser tab</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => window.open(objectUrl, '_blank', 'noopener noreferrer')}
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FiExternalLink className="w-4 h-4" /> Open PDF
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {downloading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                    Download
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full p-4">
                <img src={objectUrl} alt={doc.file_name} className="max-w-full max-h-full object-contain rounded" />
              </div>
            )
          ) : (
            /* No preview available — Excel, Word, etc. */
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-gray-400">
              {(() => {
                const cfg = typeLabels[fileType] || typeLabels.other;
                const Icon = cfg.icon;
                return (
                  <>
                    <Icon className={`w-12 h-12 ${cfg.color}`} />
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-700">{cfg.label}</p>
                      <p className="text-xs text-gray-400 mt-1">Preview not available — download to open</p>
                    </div>
                    <button
                      onClick={handleDownload}
                      disabled={downloading}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {downloading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
                      Download {cfg.label}
                    </button>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
